// src/components/AccountMenu/AccountMenu.jsx
// Profile icon with dropdown menu for account settings and subscription management
// ✅ UPDATED: Handles native apps by opening web browser for subscription management

import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useSubscription } from '../../hooks/useSubscription';
import SignUpModal from '../Auth/SignUpModal';
import FeedbackModal from './FeedbackModal';
import './AccountMenu.css';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://vivajour.com';

// ✅ Detect if running in native Capacitor app (Android or iOS)
function isNativeApp() {
  return !!window?.Capacitor?.isNativePlatform?.();
}

// ✅ Open URL in external browser (for native apps)
async function openInExternalBrowser(url) {
  try {
    const Browser = window?.Capacitor?.Plugins?.Browser;
    if (Browser?.open) {
      await Browser.open({ url, windowName: '_system' });
      return true;
    }
    const App = window?.Capacitor?.Plugins?.App;
    if (App?.openUrl) {
      await App.openUrl({ url });
      return true;
    }
    window.open(url, '_system');
    return true;
  } catch (error) {
    console.error('[AccountMenu] Failed to open external browser:', error);
    window.open(url, '_blank');
    return true;
  }
}

export default function AccountMenu() {
  const { user, isAuthenticated, signOut } = useAuth();
  const { subscription, isPro, isTrialing, isEarlyAdopter, openUpgradeModal } = useSubscription();
  const [isOpen, setIsOpen] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const menuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle opening Stripe Customer Portal
  const handleManageSubscription = async () => {
    if (!user?.uid) return;
    
    // ✅ On native apps, open web portal in external browser
    if (isNativeApp()) {
      const portalUrl = `${SITE_URL}/account?manage=true&userId=${user.uid}`;
      console.log('[AccountMenu] Native app detected, opening portal in browser:', portalUrl);
      setIsOpen(false);
      await openInExternalBrowser(portalUrl);
      return;
    }
    
    // Web: Use API to create portal session
    try {
      setPortalLoading(true);
      const response = await fetch(`${API_BASE}/api/create-portal-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid }),
      });

      if (!response.ok) {
        throw new Error('Failed to create portal session');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      console.error('[AccountMenu] Portal error:', error);
      alert('Unable to open subscription management. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut();
      setIsOpen(false);
    } catch (error) {
      console.error('[AccountMenu] Sign out error:', error);
    }
  };

  // Format trial end date
  const formatTrialEnd = () => {
    if (!subscription?.trialEndsAt) return null;
    try {
      const date = subscription.trialEndsAt.toDate 
        ? subscription.trialEndsAt.toDate() 
        : new Date(subscription.trialEndsAt);
      return date.toLocaleDateString(undefined, { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch {
      return null;
    }
  };

  // Get status badge info
  const getStatusBadge = () => {
    if (!isAuthenticated) return null;
    
    if (isPro && isTrialing) {
      return { label: 'Trial', className: 'badge-trial' };
    }
    if (isPro) {
      return { label: 'Pro', className: 'badge-pro' };
    }
    return { label: 'Free', className: 'badge-free' };
  };

  const statusBadge = getStatusBadge();
  const trialEndDate = formatTrialEnd();

  return (
    <div className="account-menu" ref={menuRef}>
      {/* Profile Icon Button */}
      <button 
        className="account-menu-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Account menu"
        aria-expanded={isOpen}
      >
        {isAuthenticated ? (
          <div className="avatar">
            {user?.email?.charAt(0).toUpperCase() || '?'}
            {isPro && <span className="pro-dot" />}
          </div>
        ) : (
          <div className="avatar avatar-anonymous">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="account-dropdown">
          {isAuthenticated ? (
            <>
              {/* User Info Section */}
              <div className="dropdown-header">
                <div className="user-email">{user?.email}</div>
                <div className="status-row">
                  {statusBadge && (
                    <span className={`status-badge ${statusBadge.className}`}>
                      {statusBadge.label}
                    </span>
                  )}
                  {isEarlyAdopter && (
                    <span className="early-adopter-badge">
                      ⭐ Early Adopter #{subscription?.earlyAdopterNumber || ''}
                    </span>
                  )}
                </div>
                {isTrialing && trialEndDate && (
                  <div className="trial-info">
                    Trial ends {trialEndDate}
                  </div>
                )}
              </div>

              <div className="dropdown-divider" />

              {/* Menu Items */}
              <div className="dropdown-items">
                {isPro ? (
                  <button 
                    className="dropdown-item"
                    onClick={handleManageSubscription}
                    disabled={portalLoading}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                      <line x1="1" y1="10" x2="23" y2="10" />
                    </svg>
                    {portalLoading ? 'Loading...' : 'Manage Subscription'}
                  </button>
                ) : (
                  <button 
                    className="dropdown-item upgrade-item"
                    onClick={() => {
                      setIsOpen(false);
                      openUpgradeModal();
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                    Upgrade to Pro
                  </button>
                )}

                <button
                  className="dropdown-item feedback-item"
                  onClick={() => {
                    setIsOpen(false);
                    setShowFeedback(true);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  Send Feedback
                </button>

                <button
                  className="dropdown-item signout-item"
                  onClick={handleSignOut}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  Sign Out
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Anonymous User */}
              <div className="dropdown-header">
                <div className="anonymous-label">Not signed in</div>
                <div className="anonymous-hint">
                  {isNativeApp()
                    ? "Sign in to sync your subscription and trips"
                    : "Sign in to sync your trips and unlock Pro features"
                  }
                </div>
              </div>

              <div className="dropdown-divider" />

              <div className="dropdown-items">
                <button
                  className="dropdown-item signin-item"
                  onClick={() => {
                    setIsOpen(false);
                    setShowSignUp(true);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                    <polyline points="10 17 15 12 10 7" />
                    <line x1="15" y1="12" x2="3" y2="12" />
                  </svg>
                  {isNativeApp() ? "Sign In" : "Sign In / Sign Up"}
                </button>

                <button
                  className="dropdown-item feedback-item"
                  onClick={() => {
                    setIsOpen(false);
                    setShowFeedback(true);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  Send Feedback
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Sign Up Modal */}
      <SignUpModal
        isOpen={showSignUp}
        onClose={() => setShowSignUp(false)}
        onSuccess={() => setShowSignUp(false)}
      />

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedback}
        onClose={() => setShowFeedback(false)}
      />
    </div>
  );
}